import { translate } from '@jsverse/transloco';

import { CustomFieldsService } from 'app/modules/field/custom-fields.service';
import { StorageAccountService } from 'app/providers/storage-api/blender-account.service';
import { WidgetDataSource } from '../../../models';
export interface AnalyticItemType {
  analyticItem: string;
  displayName: string;
  subTypes?: string[];
  taxonomyOptions?: string[];
}

export interface AnalyticItemTypeMap {
  [key: string]: AnalyticItemType;
}

export class AnalyticItemTypes {
  public static readonly defaultEntityTypes = [
    'Company',
    'Person',
    'Product',
    'Hashtag',
    'Place',
    'Quote',
  ];
  public static readonly defaultTopicTypes = ['auto', 'concept', 'manual', 'model', 'query'];
  public static readonly defaultTaxonomyTypes = ['Leaf Nodes', 'Non-Leaf Nodes'];
  public static readonly topicModelTypes = ['Intent', 'Effort', 'Emotion'];

  public static defaultAnalyticItemTypes(): AnalyticItemTypeMap {
    return {
      topics: {
        analyticItem: 'topics',
        displayName: 'Topics',
        subTypes: [],
      },
      entities: {
        analyticItem: 'entities',
        displayName: 'Entities',
        subTypes: [],
      },
      phrases: {
        analyticItem: 'phrases',
        displayName: 'Phrases',
      },
      themes: {
        analyticItem: 'themes',
        displayName: 'Themes',
      },
      taxonomy: {
        analyticItem: 'taxonomy',
        displayName: 'Taxonomy Nodes',
      },
      documents: {
        analyticItem: 'documents',
        displayName: 'Documents',
      },
      metadata: {
        analyticItem: 'metadata',
        displayName: 'Custom Fields',
      },
      standardFields: {
        analyticItem: 'standard Fields',
        displayName: 'Standard Fields',
      },
      sectionProperties: {
        analyticItem: 'sectionProperties',
        displayName: 'Section Properties',
        subTypes: [],
      },
    };
  }

  public static async generateMapFromDataSource(
    dataSource: WidgetDataSource,
    accountService: StorageAccountService,
    customFieldService: CustomFieldsService
  ): Promise<AnalyticItemTypeMap> {
    const analyticItemMap = this.defaultAnalyticItemTypes();

    Object.keys(analyticItemMap).forEach(
      (k) => (analyticItemMap[k].displayName = translate(`analytic-items.${k}`))
    );

    analyticItemMap.taxonomy.taxonomyOptions = this.defaultTaxonomyTypes;

    if (!dataSource) {
      return analyticItemMap;
    }

    if (dataSource.entityTypes && dataSource.entityTypes.length > 0) {
      analyticItemMap.entities.subTypes = dataSource.entityTypes;
    } else if (dataSource.sourceVersion === 1) {
      analyticItemMap.entities.subTypes = this.defaultEntityTypes;
    }

    if (dataSource.customFields && dataSource.sourceVersion > 1) {
      analyticItemMap.metadata.subTypes =
        await customFieldService.getMetadataSubTypesAsync(dataSource);
    } else if (dataSource.sourceVersion === 1) {
      const metadata = await accountService.groupFieldsSub.latest();
      const collectionNames = dataSource.collectionName
        ? [dataSource.collectionName]
        : dataSource.groupCollections;
      analyticItemMap.metadata.subTypes = metadata.metadata
        .filter((x) => {
          for (const collection of collectionNames) {
            if (x.documentCount[collection] && x.documentCount[collection] > 0) {
              return true;
            }
          }
          return false;
        })
        .map((meta) => meta.fieldName);
    }

    if (dataSource.sourceVersion >= 5) {
      analyticItemMap.standardFields.subTypes = Object.assign([], dataSource.standardFields);

      //Remove certain elements for now until we decide what we want to do with them
      this.pruneAnalyticItemStandardField(analyticItemMap, 'parentId');
      this.pruneAnalyticItemStandardField(analyticItemMap, 'url');
      this.pruneAnalyticItemStandardField(analyticItemMap, 'title');
    } else {
      analyticItemMap.standardFields.subTypes = [];
    }

    if (dataSource.sourceVersion >= 6 && dataSource.topicTypes?.length) {
      let topicTypes = dataSource.topicTypes;
      if (dataSource.clauseModelTypes) {
        const clauseModelTypes = dataSource.clauseModelTypes;
        topicTypes = topicTypes.concat(
          clauseModelTypes.filter((item) => this.topicModelTypes.includes(item))
        );
      }
      const finalTypes = [];
      const excludedTypes = ['clause_models', 'sentiment', 'sentiment_score'];
      topicTypes.forEach((item, _index) => {
        if (!excludedTypes.includes(item.toLowerCase())) {
          finalTypes.push(item);
        }
      });
      analyticItemMap.topics.subTypes = [...finalTypes];
    } else {
      analyticItemMap.topics.subTypes = this.defaultTopicTypes;
    }

    return analyticItemMap;
  }

  private static pruneAnalyticItemStandardField(
    analyticItemMap: AnalyticItemTypeMap,
    standardField: string
  ) {
    const idx = analyticItemMap.standardFields.subTypes.indexOf(standardField);
    if (idx > -1) {
      analyticItemMap.standardFields.subTypes.splice(idx, 1);
    }
  }
}
